/**
 * Centralized route path constants. Import these everywhere instead of
 * hardcoding strings so paths can be refactored in one place.
 */

export const PATHS = Object.freeze({
  // Public
  LANDING: '/',
  NOT_FOUND: '*',
  UNAUTHORIZED: '/unauthorized',

  // Public portfolio (unauthenticated, shareable) — never carries the student's
  // full name; only public + approved projects are reachable.
  PUBLIC_PORTFOLIO: '/p/:shortCode',
  PUBLIC_PROJECT: '/p/:shortCode/:projectId',

  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',

  // Join redirect — any signed-in role. Email "Join" CTAs land here so the
  // participant's join time is stamped on the class before we forward them to
  // the actual meeting link (see JoinClassPage).
  JOIN_CLASS: '/join/:classId',

  // Student area
  STUDENT_ROOT: '/student',
  STUDENT_DASHBOARD: '/student/dashboard',
  STUDENT_CLASSES: '/student/classes',
  STUDENT_CALENDAR: '/student/calendar',
  STUDENT_PROGRESS: '/student/progress',
  STUDENT_RECORDS: '/student/session-log',
  STUDENT_MENTOR: '/student/mentor',
  STUDENT_RESOURCES: '/student/resources',
  STUDENT_AVAILABILITY: '/student/availability',
  STUDENT_REQUESTS: '/student/requests',
  STUDENT_PORTFOLIO: '/student/portfolio',

  // Mentor area
  MENTOR_ROOT: '/mentor',
  MENTOR_DASHBOARD: '/mentor/dashboard',
  MENTOR_STUDENTS: '/mentor/students',
  // One student's full record with this mentor — build it with mentorStudentPath().
  MENTOR_STUDENT: '/mentor/students/:studentId',
  MENTOR_CLASSES: '/mentor/classes',
  MENTOR_CALENDAR: '/mentor/calendar',
  MENTOR_AVAILABILITY: '/mentor/availability',
  MENTOR_SLOTS: '/mentor/open-slots',
  MENTOR_RECORDS: '/mentor/session-log',
  MENTOR_PAYOUTS: '/mentor/payouts',
  MENTOR_RESOURCES: '/mentor/resources',
  MENTOR_REQUESTS: '/mentor/requests',
  MENTOR_PORTFOLIO: '/mentor/portfolio',

  // Admin area
  ADMIN_ROOT: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_STUDENTS: '/admin/students',
  // One student's full record: profile, courses, every class and its outcome.
  ADMIN_STUDENT: '/admin/students/:studentId',
  ADMIN_MENTORS: '/admin/mentors',
  ADMIN_ACCOUNTS: '/admin/accounts',
  ADMIN_COURSES: '/admin/courses',
  ADMIN_PRESENTATIONS: '/admin/presentations',
  ADMIN_ENROLLMENTS: '/admin/enrollments',
  // One-shot intake: account + record + course + schedule, created together.
  ADMIN_ENROL: '/admin/enrol',
  ADMIN_ASSIGN: '/admin/assign',
  ADMIN_SCHEDULE: '/admin/schedule',
  ADMIN_RESCHEDULE: '/admin/reschedule',
  ADMIN_CALENDAR: '/admin/calendar',
  ADMIN_SESSION_LOGS: '/admin/session-logs',
  ADMIN_DISPUTES: '/admin/disputes',
  ADMIN_REPORTS: '/admin/reports',
  ADMIN_SETTINGS: '/admin/settings',
});

/** The admin's page for one student: `/admin/students/{id}`. */
export function adminStudentPath(studentId) {
  return PATHS.ADMIN_STUDENT.replace(':studentId', encodeURIComponent(studentId));
}

/** The mentor's page for one student: `/mentor/students/{id}`. */
export function mentorStudentPath(studentId) {
  return PATHS.MENTOR_STUDENT.replace(':studentId', encodeURIComponent(studentId));
}
